import { Link } from 'react-router-dom';
import { cn } from '@/lib/utils';

// Route tabs — the underline strip that splits ONE screen across sibling
// routes (Website | Domain, Profile | Content | Map). Extracted in the design
// audit (T7/B5) from the two hand-rolled copies that had drifted apart in
// markup while looking identical.
//
// Two rules this primitive exists to keep:
//   1. It is a real <nav> with an accessible name, and the active tab carries
//      `aria-current="page"` — a tab strip built from bare links tells a
//      screen-reader user nothing about where they are.
//   2. `border-b-2` here is an UNDERLINE tab affordance, not the banned
//      side-stripe: it sits under the label, not beside it. Do not turn it
//      into `border-l-2` anywhere.
//
// Not for in-page state switching — that is a SegmentedControl
// (components/inspector/segmented-control.tsx). This one navigates.

export interface RouteTab {
  key: string;
  label: string;
  to: string;
}

export function RouteTabs({
  ariaLabel,
  tabs,
  activeKey,
  className,
}: {
  ariaLabel: string;
  tabs: ReadonlyArray<RouteTab>;
  activeKey: string;
  className?: string;
}) {
  return (
    <nav aria-label={ariaLabel} className={cn('flex gap-4 border-b border-border-subtle', className)}>
      {tabs.map((tab) => {
        const active = tab.key === activeKey;
        return (
          <Link
            key={tab.key}
            to={tab.to}
            aria-current={active ? 'page' : undefined}
            className={cn(
              'border-b-2 px-1 pb-2 text-sm font-medium',
              'transition-colors duration-[var(--duration-fast)]',
              'focus-visible:outline-2 focus-visible:outline-brand-500 focus-visible:outline-offset-2',
              active
                ? 'border-brand-500 text-foreground'
                : 'border-transparent text-muted-foreground hover:text-foreground',
            )}
          >
            {tab.label}
          </Link>
        );
      })}
    </nav>
  );
}
